import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, KeyRound, Loader2, Pencil, Plus, Printer, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

import * as adminApi from "@/api/admin";
import * as fbs from "@/api/fbs";
import * as pickersApi from "@/api/one_c_pickers";
import * as ozon from "@/api/ozon_fbs";
import * as settingsApi from "@/api/system_settings";
import { PickerField } from "@/components/pickers/PickerField";
import { savedPickerValue } from "@/components/pickers/RemotePicker";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { extractErrorMessage, toastError } from "@/lib/api-errors";
import { Dialog } from "@/lib/scan-ui/overlays";
import { usePortalConfig } from "@/portal/usePortalConfig";

/**
 * Точки сборки ФБС: помещения со своим relay-агентом и своими принтерами.
 *
 * Иерархия: точка → комнаты (склады 1С) → склады ВБ и Ozon, которые в комнату
 * «смотрят» (склад 1С задаётся складу в карточке кабинета). Точка склада МП
 * отдельно не задаётся — она выводится через его комнату.
 *
 * Точек нет — раздел работает ровно как раньше (все сборщики видят все склады,
 * печать на общем принтере). Это не «не настроено», а нормальный режим
 * инсталляции с одной комнатой, поэтому пустой список не ругается.
 *
 * ⚠️ Комната принадлежит НЕ БОЛЕЕ ЧЕМ ОДНОЙ точке — это держит первичный ключ
 * на бэкенде. Галка чужой комнаты здесь недоступна и называет владельца: иначе
 * единственным проявлением ошибки был бы 409 без объяснения.
 */
export function FbsPointsCard(): React.ReactElement {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<PointFormState | null>(null);
  // Токен показывается ОДИН раз — держим его в состоянии карточки до закрытия
  // модалки. В базе от него только хэш, второго показа не будет.
  const [token, setToken] = useState<fbs.PointRelayToken | null>(null);
  const [toDelete, setToDelete] = useState<fbs.FbsPoint | null>(null);

  const { hasPage } = usePortalConfig();
  // Раздела Ozon в инсталляции нет — его роутер не смонтирован, и складов Ozon,
  // которые смотрели бы в комнату, не существует вовсе.
  const hasOzon = hasPage("ozon_fbs");

  const pointsQ = useQuery({ queryKey: ["fbs-points"], queryFn: fbs.listPoints });
  // Комнаты вне точек: склады в них не видит НИ ОДИН сборщик, и заметить это
  // можно только здесь. Сервер отдаёт пустые списки, когда точек нет вовсе.
  const unassignedQ = useQuery({
    queryKey: ["fbs-points-unassigned"],
    queryFn: fbs.listUnassigned,
  });
  // Какие склады смотрят в комнату — у кабинетов, тем же запросом (и ключом),
  // что и их карточки.
  const cabinetsQ = useQuery({ queryKey: ["fbs-cabinets"], queryFn: fbs.listCabinets });
  const ozonCabinetsQ = useQuery({
    queryKey: ["ozon", "cabinets"],
    queryFn: ozon.listCabinets,
    enabled: hasOzon,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["fbs-points"] });
    qc.invalidateQueries({ queryKey: ["fbs-points-unassigned"] });
    qc.invalidateQueries({ queryKey: ["fbs-print-status"] });
  };

  const createMut = useMutation({
    mutationFn: (form: PointFormState) =>
      fbs.createPoint({
        name: form.name,
        relay_agent: form.relay_agent,
        printers: formPrinters(form),
        position: form.position,
        // Люди уходят ПОЛНЫМ составом уже при заведении: карточка
        // пользователя их больше не знает, второго места для привязки нет.
        user_ids: form.user_ids,
        // Пару «ссылка + имя» шлём ТОЛЬКО когда склад выбрали: пустое
        // переопределение и есть «как в системных настройках».
        ...(form.source_wh_ref.trim()
          ? {
              source_wh_ref: form.source_wh_ref.trim(),
              source_wh_name: form.source_wh_name.trim(),
            }
          : {}),
      }),
    onSuccess: () => {
      invalidate();
      setDraft(null);
      toast.success("Точка заведена. Теперь укажите её комнаты кнопкой «Изменить»");
    },
    onError: toastError("Не удалось завести точку"),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => fbs.deletePoint(id),
    onSuccess: () => {
      invalidate();
      setToDelete(null);
      toast.success("Точка удалена — её склады снова видны всем");
    },
    onError: toastError("Не удалось удалить точку"),
  });

  const points = pointsQ.data ?? [];
  const unassigned = unassignedQ.data;
  const roomIndex = buildRoomIndex(cabinetsQ.data ?? [], ozonCabinetsQ.data ?? []);
  const withoutRoom = unassigned
    ? [
        ...unassigned.warehouses_without_room.map(
          (w) => `${w.name || w.wb_warehouse_id} (WB, ${w.cabinet_name})`,
        ),
        ...unassigned.ozon_warehouses_without_room.map(
          (w) => `${w.name || w.ozon_warehouse_id} (Ozon, ${w.cabinet_name})`,
        ),
      ]
    : [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Точки сборки</CardTitle>
        <Button size="sm" variant="outline" onClick={() => setDraft({ ...EMPTY_POINT })}>
          <Plus className="mr-1 size-4" /> Добавить
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Точка — помещение со своим компьютером, relay-агентом и принтерами.
          В точке — комнаты (склады 1С), в комнату смотрят склады WB и Ozon.
          Сборщик видит только склады комнат своих точек, стикер печатается на
          принтере той точки, где идёт сбор. Нет точек — все сборщики видят все
          склады, печать на общем принтере.
        </p>

        {pointsQ.isLoading && <Loader2 className="size-4 animate-spin" />}
        {pointsQ.isError && (
          <p className="rounded bg-rose-50 px-2 py-1 text-sm text-rose-700">
            Не удалось загрузить точки: {extractErrorMessage(pointsQ.error)}
          </p>
        )}
        {cabinetsQ.isError && (
          <p className="rounded bg-rose-50 px-2 py-1 text-sm text-rose-700">
            Не удалось загрузить склады кабинетов:{" "}
            {extractErrorMessage(cabinetsQ.error)} — подписи комнат неполные.
          </p>
        )}
        {ozonCabinetsQ.isError && (
          <p className="rounded bg-rose-50 px-2 py-1 text-sm text-rose-700">
            Не удалось загрузить склады кабинетов Ozon:{" "}
            {extractErrorMessage(ozonCabinetsQ.error)} — подписи комнат неполные.
          </p>
        )}
        {unassignedQ.isError && (
          <p className="rounded bg-rose-50 px-2 py-1 text-sm text-rose-700">
            Не удалось проверить комнаты вне точек:{" "}
            {extractErrorMessage(unassignedQ.error)} — добавить в точку новую
            комнату нельзя.
          </p>
        )}
        {points.length > 0 && unassigned && unassigned.rooms.length > 0 && (
          <p
            role="alert"
            className="rounded bg-amber-50 px-2 py-1 text-sm text-amber-800"
          >
            Комнаты вне точек — их склады сборщики не видят:{" "}
            {unassigned.rooms
              .map((r) => {
                const whs = unassignedRoomWarehouses(r);
                return `${roomTitle(r)}${whs ? ` (${whs})` : ""}`;
              })
              .join("; ")}
            . Добавьте их в нужную точку кнопкой «Изменить».
          </p>
        )}
        {points.length > 0 && withoutRoom.length > 0 && (
          <p
            role="alert"
            className="rounded bg-amber-50 px-2 py-1 text-sm text-amber-800"
          >
            Склады без склада 1С — в точку их не отдать, сборщики их не видят:{" "}
            {withoutRoom.join(", ")}. Задайте складу склад 1С (комнату) в карточке
            его кабинета — тогда комната появится здесь.
          </p>
        )}

        {points.map((point) => (
          <PointRow
            key={point.id}
            point={point}
            points={points}
            roomIndex={roomIndex}
            freeRooms={unassigned?.rooms ?? []}
            hasOzon={hasOzon}
            onDelete={() => setToDelete(point)}
            onToken={setToken}
            onSaved={invalidate}
          />
        ))}

        {draft && (
          <PointForm
            value={draft}
            hasOzon={hasOzon}
            onChange={setDraft}
            onCancel={() => setDraft(null)}
            onSave={() => createMut.mutate(draft)}
            saving={createMut.isPending}
          />
        )}
      </CardContent>

      {token && <RelayTokenDialog token={token} onClose={() => setToken(null)} />}
      {toDelete && (
        <Dialog
          title={`Удалить точку «${toDelete.name}»?`}
          body={
            <>
              Пропадут её комнаты и привязки людей; агент «{toDelete.relay_agent}»
              перестанет подключаться. Склады её комнат вернутся в общий доступ — если
              точек не останется вовсе, раздел будет работать как до их
              появления.
            </>
          }
          confirmLabel="Удалить"
          danger
          confirmBusy={deleteMut.isPending}
          onConfirm={() => deleteMut.mutate(toDelete.id)}
          onCancel={() => setToDelete(null)}
        />
      )}
    </Card>
  );
}

/** Состояние формы точки. Одно на заведение и правку: поля те же, а
 *  `room_refs` у новой точки не заполняется — состав задаётся правкой (пока
 *  точек нет, сервер не отдаёт и списка свободных комнат, выбирать не из чего).
 *  Люди, наоборот, задаются сразу: другого места для привязки нет. */
interface PointFormState {
  name: string;
  relay_agent: string;
  /** Принтер назначения `fbs` — обязателен. */
  printer_fbs: string;
  /** Принтер назначения `ozon` — необязателен; пусто = у точки его нет. */
  printer_ozon: string;
  position: number;
  /** Склад-источник завоза точки. Пусто — «как в системных настройках». */
  source_wh_ref: string;
  source_wh_name: string;
  /** Комнаты (ссылки складов 1С) — ПОЛНЫЙ состав. */
  room_refs?: string[];
  /** Люди точки — ПОЛНЫЙ состав, как комнаты. */
  user_ids: number[];
}

const EMPTY_POINT: PointFormState = {
  name: "",
  relay_agent: "",
  printer_fbs: "",
  printer_ozon: "",
  position: 0,
  source_wh_ref: "",
  source_wh_name: "",
  user_ids: [],
};

/** Принтеры формы → `printers` (ПОЛНАЯ замена). Пустой принтер Ozon не
 *  уходит вовсе: сервер пустой адрес отбивает 422, а отсутствие назначения и
 *  значит «у точки принтера Ozon нет». */
function formPrinters(form: PointFormState): fbs.PointPrinters {
  const ozonTarget = form.printer_ozon.trim();
  return { fbs: form.printer_fbs.trim(), ...(ozonTarget ? { ozon: ozonTarget } : {}) };
}

/**
 * Форма точки → тело `PUT`.
 *
 * ⚠️ Пара «источник» уходит ТОЛЬКО когда её меняли: для сервера `null` —
 * значащее значение («сбросить на системную настройку»), а отсутствие поля —
 * «не трогать». Слать `null` всегда значило бы стирать источник при любой
 * правке имени, если карточка открылась до его загрузки.
 */
function toUpdatePayload(
  form: PointFormState,
  point: fbs.FbsPoint,
): fbs.PointUpdatePayload {
  const { source_wh_ref, source_wh_name, printer_fbs: _f, printer_ozon: _o, ...base } = form;
  const rest = { ...base, printers: formPrinters(form) };
  const ref = source_wh_ref.trim();
  if (ref === (point.source_wh_ref ?? "").trim()) return rest;
  return {
    ...rest,
    source_wh_ref: ref || null,
    source_wh_name: source_wh_name.trim() || null,
  };
}

/** Склад-источник завоза 1С — тот же пикер, что у системной настройки. */
const searchWarehouses = (q: string) => pickersApi.searchStructuralUnits("Склад", q);

/**
 * Имя склада-источника из СИСТЕМНЫХ настроек — умолчание для точек без своего.
 *
 * Называем его прямо на экране: «переопределение» без названного умолчания
 * читалось бы как «поедет неизвестно откуда». Запрос тот же и с тем же ключом,
 * что у карточки ссылок 1С на этой же странице, — react-query отдаёт его из
 * кэша, второго обращения к серверу нет.
 *
 * Ошибку загрузки НЕ подменяем словами «не задан»: «настройка пустая» и
 * «настройку не спросили» — разные вещи, и вторая означала бы, что подпись
 * врёт про боевой склад.
 */
function useSystemSourceHint(): string {
  const q = useQuery({
    queryKey: ["system-settings"],
    queryFn: settingsApi.getSystemSettings,
  });
  if (q.isError) {
    return `системные настройки не прочитаны: ${extractErrorMessage(q.error)}`;
  }
  if (!q.data) return "как в системных настройках: …";
  const name = (q.data.fbs_source_wh_name ?? "").trim();
  return `как в системных настройках: ${name || "не задан"}`;
}

/** Откуда поедет завоз в склады ЭТОЙ точки — строкой в списке. */
function PointSourceLine({ point }: { point: fbs.FbsPoint }): React.ReactElement {
  const hint = useSystemSourceHint();
  const own = (point.source_wh_ref ?? "").trim();
  return (
    <div className="text-xs text-muted-foreground">
      Источник завоза: {own ? point.source_wh_name || own : hint}
    </div>
  );
}

/** Агент, который ходит по общему `HS_RELAY_TOKEN` и своего токена не имеет. */
const DEFAULT_AGENT = "default";

/** Ключ комнаты: ссылку сравниваем без регистра (сервер делает так же —
 *  `points._room_key`), иначе одна комната в двух написаниях задвоилась бы. */
const roomKey = (ref: string): string => ref.trim().toLowerCase();

/** Комната точки, в которую не смотрит ни один склад активного кабинета.
 *  Сервер такую комнату точке оставляет (иначе любая правка точки давала бы
 *  409, пока её не снимут), а мы называем — чтобы админ видел и мог снять. */
const ORPHAN_ROOM = "ни один склад в неё больше не смотрит";

/** Склады, которые смотрят в комнату, — подписями для человека. */
interface RoomWarehouses {
  wb: string[];
  ozon: string[];
}

type RoomIndex = Map<string, RoomWarehouses>;

/** Комната → склады ВБ и Ozon, у которых в кабинете выбран этот склад 1С.
 *  Только АКТИВНЫЕ кабинеты — тот же критерий, что у сервера
 *  (`points._cabinet_warehouses_with_rooms`): выключенный кабинет в работе не
 *  участвует, и его склад комнату «живой» не делает. */
function buildRoomIndex(
  cabinets: fbs.Cabinet[],
  ozonCabinets: ozon.OzonCabinet[],
): RoomIndex {
  const index: RoomIndex = new Map();
  const slot = (ref: string): RoomWarehouses => {
    const key = roomKey(ref);
    let entry = index.get(key);
    if (!entry) {
      entry = { wb: [], ozon: [] };
      index.set(key, entry);
    }
    return entry;
  };
  for (const c of cabinets) {
    if (!c.is_active) continue;
    for (const w of c.warehouses) {
      if (!w.one_c_warehouse_ref) continue;
      slot(w.one_c_warehouse_ref).wb.push(`${w.name || w.wb_warehouse_id} (${c.name})`);
    }
  }
  for (const c of ozonCabinets) {
    if (!c.is_active) continue;
    for (const w of c.warehouses) {
      if (!w.one_c_warehouse_ref) continue;
      slot(w.one_c_warehouse_ref).ozon.push(`${w.name || w.ozon_warehouse_id} (${c.name})`);
    }
  }
  return index;
}

/** «WB: …; Ozon: …» — или пусто, если в комнату не смотрит ни один склад. */
function warehousesText(wh: RoomWarehouses | undefined): string {
  if (!wh) return "";
  return [
    wh.wb.length ? `WB: ${wh.wb.join(", ")}` : "",
    wh.ozon.length ? `Ozon: ${wh.ozon.join(", ")}` : "",
  ]
    .filter(Boolean)
    .join("; ");
}

/** Склады ничьей комнаты — из ответа `unassigned` (он их и перечисляет). */
function unassignedRoomWarehouses(room: fbs.PointUnassignedRoom): string {
  return warehousesText({
    wb: room.warehouses.map((w) => `${w.name || w.wb_warehouse_id} (${w.cabinet_name})`),
    ozon: room.ozon_warehouses.map(
      (w) => `${w.name || w.ozon_warehouse_id} (${w.cabinet_name})`,
    ),
  });
}

/** Имя комнаты; пустое имя допустимо (миграция), тогда показываем ссылку —
 *  пустая строка врала бы. */
const roomTitle = (r: { one_c_warehouse_ref: string; name: string }): string =>
  r.name || r.one_c_warehouse_ref;

function PointRow({
  point,
  points,
  roomIndex,
  freeRooms,
  hasOzon,
  onDelete,
  onToken,
  onSaved,
}: {
  point: fbs.FbsPoint;
  points: fbs.FbsPoint[];
  roomIndex: RoomIndex;
  freeRooms: fbs.PointUnassignedRoom[];
  hasOzon: boolean;
  onDelete: () => void;
  onToken: (t: fbs.PointRelayToken) => void;
  onSaved: () => void;
}): React.ReactElement {
  const [form, setForm] = useState<PointFormState | null>(null);

  const updateMut = useMutation({
    mutationFn: (form: PointFormState) =>
      fbs.updatePoint(point.id, toUpdatePayload(form, point)),
    onSuccess: () => {
      onSaved();
      setForm(null);
      toast.success("Сохранено");
    },
    onError: toastError("Не удалось сохранить точку"),
  });

  const tokenMut = useMutation({
    mutationFn: () => fbs.issuePointRelayToken(point.id),
    onSuccess: (t) => {
      onToken(t);
      onSaved();
    },
    onError: toastError("Не удалось выпустить токен"),
  });

  const testMut = useMutation({
    mutationFn: (purpose: "fbs" | "ozon") => fbs.printTestLabel(purpose, point.id),
    onSuccess: (r) =>
      r.ok
        ? toast.success("Задание ушло на принтер точки — проверьте, что этикетка СКАНИРУЕТСЯ")
        : toast.error(r.error ?? "Печать не удалась"),
    onError: toastError("Ошибка печати"),
  });

  const isDefaultAgent = point.relay_agent === DEFAULT_AGENT;

  return (
    <div className="rounded-lg border p-3 text-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium">{point.name}</div>
          <div className="text-xs text-muted-foreground">
            Агент: <code>{point.relay_agent}</code> · принтер ФБС WB:{" "}
            <code>{point.printers.fbs}</code>
            {(hasOzon || point.printers.ozon) && (
              <>
                {" "}· принтер Ozon:{" "}
                {point.printers.ozon ? (
                  <code>{point.printers.ozon}</code>
                ) : (
                  <span className="text-amber-700">
                    не задан — этикетки Ozon складов точки не напечатаются
                  </span>
                )}
              </>
            )}
          </div>
          <PointRoomsLine point={point} roomIndex={roomIndex} />
          <PointUsersLine point={point} />
          <PointSourceLine point={point} />
        </div>
        <div className="flex shrink-0 gap-1">
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              setForm(
                form
                  ? null
                  : {
                      name: point.name,
                      relay_agent: point.relay_agent,
                      printer_fbs: point.printers.fbs,
                      printer_ozon: point.printers.ozon ?? "",
                      position: point.position,
                      source_wh_ref: point.source_wh_ref ?? "",
                      source_wh_name: point.source_wh_name ?? "",
                      room_refs: point.rooms.map((r) => r.one_c_warehouse_ref),
                      user_ids: [...point.user_ids],
                    },
              )
            }
          >
            <Pencil className="mr-1 size-4" /> {form ? "Свернуть" : "Изменить"}
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete}>
            <Trash2 className="size-4 text-destructive" />
          </Button>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <PointAgentStatus point={point} />
        <Button
          size="sm"
          variant="outline"
          onClick={() => testMut.mutate("fbs")}
          disabled={testMut.isPending}
        >
          <Printer className="mr-1 size-4" /> Тестовая печать
        </Button>
        {point.printers.ozon && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => testMut.mutate("ozon")}
            disabled={testMut.isPending}
          >
            <Printer className="mr-1 size-4" /> Тест принтера Ozon
          </Button>
        )}
        {isDefaultAgent ? (
          <span className="text-xs text-muted-foreground">
            Агент <code>default</code> подключается по <code>HS_RELAY_TOKEN</code> —
            свой токен ему не выпускается.
          </span>
        ) : (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={() => tokenMut.mutate()}
              disabled={tokenMut.isPending}
            >
              <KeyRound className="mr-1 size-4" />
              {point.relay_token_issued ? "Перевыпустить токен агента" : "Выпустить токен агента"}
            </Button>
            {!point.relay_token_issued && (
              <span className="text-xs text-amber-700">
                Токен ещё не выпускался — агент «{point.relay_agent}» подключиться не сможет.
              </span>
            )}
          </>
        )}
      </div>

      {form && (
        <div className="mt-3">
          <PointForm
            value={form}
            hasOzon={hasOzon}
            onChange={setForm}
            onCancel={() => setForm(null)}
            onSave={() => updateMut.mutate(form)}
            saving={updateMut.isPending}
            roomsEditor={
              <RoomsPicker
                point={point}
                points={points}
                freeRooms={freeRooms}
                roomIndex={roomIndex}
                value={form.room_refs ?? []}
                onChange={(room_refs) => setForm({ ...form, room_refs })}
              />
            }
          />
        </div>
      )}
    </div>
  );
}

/** Состав точки словами: комнаты и склады, которые в них смотрят. Пусто —
 *  точка ни на что не влияет, и это надо сказать: её агент подключится, но ни
 *  один сборщик на неё не попадёт. */
function PointRoomsLine({
  point,
  roomIndex,
}: {
  point: fbs.FbsPoint;
  roomIndex: RoomIndex;
}): React.ReactElement {
  if (point.rooms.length === 0) {
    return (
      <div className="text-xs text-amber-700">
        Комнат нет — точка ни на что не влияет. Добавьте их кнопкой «Изменить».
      </div>
    );
  }
  return (
    <div className="text-xs text-muted-foreground">
      {point.rooms.map((r) => {
        const whs = warehousesText(roomIndex.get(roomKey(r.one_c_warehouse_ref)));
        return (
          <div key={r.one_c_warehouse_ref}>
            Комната {roomTitle(r)}:{" "}
            {whs || <span className="text-amber-700">{ORPHAN_ROOM}</span>}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Люди инсталляции для привязки к точкам.
 *
 * Ключ тот же, что у страницы пользователей, — строки точек и форма берут
 * список из одного кэша, второго запроса нет.
 */
function useUsersQuery() {
  return useQuery({ queryKey: ["admin", "users"], queryFn: adminApi.listUsers });
}

/** Люди точки словами. Пусто — её склады не соберёт НИКТО, кроме админа, и это
 *  надо сказать: точка со складами и без людей выглядит настроенной. */
function PointUsersLine({ point }: { point: fbs.FbsPoint }): React.ReactElement {
  const usersQ = useUsersQuery();
  if (point.user_ids.length === 0) {
    return (
      <div className="text-xs text-amber-700">
        Люди: никто не привязан — склады точки не увидит ни один сборщик
        (администратор видит всё).
      </div>
    );
  }
  // Имени может не быть, если список ещё не пришёл или не прочитался:
  // показываем id, а не пустоту — иначе строка врёт про состав точки.
  const names = point.user_ids.map(
    (id) => usersQ.data?.find((u) => u.id === id)?.username ?? `#${id}`,
  );
  return <div className="text-xs text-muted-foreground">Люди: {names.join(", ")}</div>;
}

/**
 * Галочки людей точки.
 *
 * Админов в списке НЕТ: они видят склады всех точек по своему признаку
 * (`fbs/points.py::allowed_warehouses`), и галка у них ничего не меняла бы.
 * Исключение — админ, уже привязанный к этой точке: его показываем, иначе
 * сохранение полным составом молча сняло бы привязку.
 *
 * Список не прочитался — блок НЕ рисуем вовсе и называем причину: пустой
 * список галочек читался бы как «людей нет», а сохранение стёрло бы состав.
 */
function UsersPicker({
  value,
  onChange,
}: {
  value: number[];
  onChange: (v: number[]) => void;
}): React.ReactElement {
  const usersQ = useUsersQuery();

  if (usersQ.isError) {
    return (
      <div className="sm:col-span-2">
        <Label className="text-xs">Люди точки</Label>
        <p className="rounded bg-rose-50 px-2 py-1 text-xs text-rose-700">
          список пользователей не загрузился: {extractErrorMessage(usersQ.error)} —
          людей точки не изменить.
        </p>
      </div>
    );
  }

  const users = (usersQ.data ?? []).filter(
    (u) => !u.is_admin || value.includes(u.id),
  );
  const toggle = (id: number) =>
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);

  return (
    <div className="sm:col-span-2">
      <Label className="text-xs">Люди точки</Label>
      <p className="text-xs text-muted-foreground">
        Сборщик видит склады только своих точек и печатает на их принтере.
        Администраторы видят всё и в списке не показаны.
      </p>
      {usersQ.isLoading && <Loader2 className="mt-1 size-4 animate-spin" />}
      {!usersQ.isLoading && users.length === 0 && (
        <p className="text-xs text-amber-700">
          Кроме администраторов, пользователей нет — привязывать некого.
        </p>
      )}
      <div className="mt-1 grid gap-1">
        {users.map((u) => (
          <label key={u.id} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4"
              checked={value.includes(u.id)}
              onChange={() => toggle(u.id)}
            />
            {u.username}
            {!u.is_active && (
              <span className="text-xs text-muted-foreground">— деактивирован</span>
            )}
          </label>
        ))}
      </div>
    </div>
  );
}

/** Агент точки подключён прямо сейчас? Спрашиваем ПРО ЭТУ точку: у соседней
 *  свой ПК, и общий индикатор показывал бы чужое состояние. */
function PointAgentStatus({ point }: { point: fbs.FbsPoint }): React.ReactElement {
  const statusQ = useQuery({
    queryKey: ["fbs-print-status", point.id],
    queryFn: () => fbs.getPrintStatus(point.id),
    refetchInterval: 30000,
  });

  if (statusQ.isError) {
    return (
      <span className="rounded bg-rose-100 px-2 py-0.5 text-xs text-rose-800">
        Статус агента недоступен: {extractErrorMessage(statusQ.error)}
      </span>
    );
  }
  if (!statusQ.data) {
    return <span className="text-xs text-muted-foreground">Статус агента…</span>;
  }
  return (
    <span
      className={
        statusQ.data.relay_connected
          ? "rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800"
          : "rounded bg-rose-100 px-2 py-0.5 text-xs text-rose-800"
      }
    >
      Агент «{point.relay_agent}»:{" "}
      {statusQ.data.relay_connected ? "подключён" : "не подключён"}
    </span>
  );
}

/**
 * Галочки комнат точки: свои, ничьи (из `unassigned`) и чужие.
 *
 * Чужая комната показана и НЕДОСТУПНА с именем владельца — «комната в одной
 * точке» иначе выяснялось бы по 409 при сохранении. Под каждой комнатой —
 * склады WB и Ozon, которые в неё смотрят: выбирают помещение, а видят склады.
 */
function RoomsPicker({
  point,
  points,
  freeRooms,
  roomIndex,
  value,
  onChange,
}: {
  point: fbs.FbsPoint;
  points: fbs.FbsPoint[];
  freeRooms: fbs.PointUnassignedRoom[];
  roomIndex: RoomIndex;
  value: string[];
  onChange: (v: string[]) => void;
}): React.ReactElement {
  const candidates: { ref: string; name: string; owner?: fbs.FbsPoint }[] = [];
  const seen = new Set<string>();
  const add = (ref: string, name: string, owner?: fbs.FbsPoint) => {
    if (seen.has(roomKey(ref))) return;
    seen.add(roomKey(ref));
    candidates.push({ ref, name, owner });
  };
  for (const r of point.rooms) add(r.one_c_warehouse_ref, r.name);
  for (const r of freeRooms) add(r.one_c_warehouse_ref, r.name);
  for (const p of points) {
    if (p.id === point.id) continue;
    for (const r of p.rooms) add(r.one_c_warehouse_ref, r.name, p);
  }
  const chosen = new Set(value.map(roomKey));

  const toggle = (ref: string) =>
    onChange(
      chosen.has(roomKey(ref))
        ? value.filter((v) => roomKey(v) !== roomKey(ref))
        : [...value, ref],
    );

  return (
    <div className="sm:col-span-2">
      <Label className="text-xs">Комнаты точки (склады 1С)</Label>
      <p className="text-xs text-muted-foreground">
        Склад WB или Ozon попадает в точку через свою комнату — склад 1С, заданный
        ему в карточке кабинета. Комната — только в одной точке.
      </p>
      {candidates.length === 0 && (
        <p className="text-xs text-muted-foreground">
          Комнат нет — сначала задайте складам кабинетов WB и Ozon склад 1С.
        </p>
      )}
      <div className="mt-1 grid gap-1">
        {candidates.map((c) => {
          const whs = warehousesText(roomIndex.get(roomKey(c.ref)));
          return (
            <label
              key={c.ref}
              className="flex items-start gap-2 text-sm"
              title={c.owner ? `Комната уже в точке «${c.owner.name}»` : undefined}
            >
              <input
                type="checkbox"
                className="mt-1"
                checked={chosen.has(roomKey(c.ref))}
                disabled={Boolean(c.owner)}
                onChange={() => toggle(c.ref)}
              />
              <span>
                {roomTitle({ one_c_warehouse_ref: c.ref, name: c.name })}
                {whs ? (
                  <span className="block text-xs text-muted-foreground">{whs}</span>
                ) : (
                  <span className="block text-xs text-amber-700">
                    {ORPHAN_ROOM}
                    {c.owner ? "" : " — снимите её, если она больше не нужна"}
                  </span>
                )}
                {c.owner && (
                  <span className="block text-xs text-amber-700">
                    уже в точке «{c.owner.name}» — комната может быть только в одной
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

/** Форма точки: редактор комнат приходит снаружи — у новой точки его нет,
 *  состав задаётся правкой (см. `PointFormState`). */
function PointForm({
  value,
  hasOzon,
  onChange,
  onCancel,
  onSave,
  saving,
  roomsEditor,
}: {
  value: PointFormState;
  /** Есть раздел Ozon — есть и поле его принтера. */
  hasOzon: boolean;
  onChange: (v: PointFormState) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  roomsEditor?: React.ReactNode;
}): React.ReactElement {
  const sourceHint = useSystemSourceHint();
  const field = (
    key: "name" | "relay_agent" | "printer_fbs" | "printer_ozon" | "position",
    label: string,
    hint: string,
    type: "text" | "number" = "text",
  ) => (
    <div>
      <Label className="text-xs" htmlFor={`point-${key}`}>
        {label}
      </Label>
      <Input
        id={`point-${key}`}
        type={type}
        value={value[key]}
        onChange={(e) =>
          onChange({
            ...value,
            [key]: type === "number" ? Number(e.target.value || 0) : e.target.value,
          })
        }
      />
      <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
    </div>
  );

  // Имя, агент и принтер обязательны сразу: точка без принтера печатать не
  // станет, а подставленный «общий» увёл бы стикер в чужое помещение.
  const filled =
    Boolean(value.name.trim()) &&
    Boolean(value.relay_agent.trim()) &&
    Boolean(value.printer_fbs.trim());

  return (
    <div className="space-y-3 rounded-lg border border-dashed p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        {field("name", "Название", "как показывать: «Володарского»")}
        {field("position", "Позиция", "порядок в списке", "number")}
        {field(
          "relay_agent",
          "Имя relay-агента",
          "default — ПК, который уже подключён по HS_RELAY_TOKEN; другому ПК придумайте имя (оно уйдёт в WH_RELAY_AGENT) и выпустите токен",
        )}
        {field(
          "printer_fbs",
          "Принтер ФБС WB",
          "стикеры заданий и этикетки коробов: tcp://192.168.1.50:9100, usb:///dev/label-printer или serial://COM3",
        )}
        {/* Показываем и без раздела Ozon, если адрес уже задан: иначе правка
            точки молча стёрла бы его полной заменой принтеров. */}
        {(hasOzon || value.printer_ozon.trim() !== "") &&
          field(
            "printer_ozon",
            "Принтер Ozon",
            "этикетки отправлений Ozon; пусто — у точки его нет, и этикетка Ozon её склада откажет с именем точки (общий принтер не подставляется)",
          )}
        <div className="sm:col-span-2">
          {/* ⚠️ Необязательное ПЕРЕОПРЕДЕЛЕНИЕ, а не фоллбэк: пусто — завоз в
              склады точки едет со склада из системных настроек, и он назван
              прямо в подписи. Крестик пикера сбрасывает переопределение. */}
          <PickerField
            label="Склад-источник завоза"
            hint={
              value.source_wh_ref.trim()
                ? "завоз в склады этой точки пойдёт с него; крестик — вернуть системный"
                : sourceHint
            }
            placeholder="Начните вводить название склада…"
            value={savedPickerValue(value.source_wh_ref, value.source_wh_name)}
            search={searchWarehouses}
            queryKey={["one-c-warehouses"]}
            onChange={(r) =>
              onChange({
                ...value,
                source_wh_ref: r?.ref ?? "",
                source_wh_name: r?.name ?? "",
              })
            }
          />
        </div>
        {roomsEditor}
        <UsersPicker
          value={value.user_ids}
          onChange={(user_ids) => onChange({ ...value, user_ids })}
        />
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={onSave} disabled={saving || !filled}>
          Сохранить
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>
          Отмена
        </Button>
      </div>
      {!filled && (
        <p className="text-xs text-amber-700">
          Название, агент и принтер ФБС WB обязательны: наследования от общего принтера нет.
        </p>
      )}
    </div>
  );
}

/**
 * Единственный показ токена агента.
 *
 * В базе от него sha256, поэтому «открыть точку и посмотреть токен ещё раз»
 * технически невозможно — образец тот же, что у QR-пропуска (`BadgeCard`).
 */
function RelayTokenDialog({
  token,
  onClose,
}: {
  token: fbs.PointRelayToken;
  onClose: () => void;
}): React.ReactElement {
  return (
    <Dialog
      title={`Токен агента «${token.agent}»`}
      body={
        <div className="space-y-2">
          <code className="block select-all break-all rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-center font-mono text-[13px]">
            {token.token}
          </code>
          <p className="text-amber-900">
            Это единственный показ токена — в базе только его отпечаток.
            Пропишите его на ПК точки в <code>WH_RELAY_TOKEN</code>, а имя агента —
            в <code>WH_RELAY_AGENT</code>. Перевыпуск ЗАМЕНЯЕТ токен: ПК со старым
            отключится при ближайшем обрыве связи.
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              void navigator.clipboard
                .writeText(token.token)
                .then(() => toast.success("Токен скопирован"))
                .catch(() =>
                  // Без https буфера обмена нет — строка выделяема (`select-all`).
                  toast.error("Буфер обмена недоступен — выделите строку и скопируйте"),
                );
            }}
          >
            <Copy className="mr-1 size-4" /> Копировать
          </Button>
        </div>
      }
      confirmLabel="Готово"
      onConfirm={onClose}
      onCancel={onClose}
    />
  );
}
